import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Grid, 
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';
// import axios from 'axios'; // Hapus import yang tidak digunakan

const Budget = () => {
  // Change from:
  const { /* user, token */ } = useAuth();
  
  // To:
  const auth = useAuth();
  // Then use auth.user and auth.token if needed
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [budgetData, setBudgetData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false); // Define the missing state
  // const [filterProject, setFilterProject] = useState(''); // Hapus variabel yang tidak digunakan
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    // Simulasi fetch data budget
    const fetchBudgetData = async () => {
      try {
        // Di sini Anda akan memanggil API untuk mendapatkan data budget
        // Untuk sementara, kita gunakan data dummy
        setTimeout(() => {
          setBudgetData({
            totalBudget: 1200000000,
            spent: 450000000,
            remaining: 750000000,
            categories: [
              { name: 'Material', allocated: 700000000, spent: 300000000 },
              { name: 'Labor', allocated: 300000000, spent: 100000000 },
              { name: 'Equipment', allocated: 150000000, spent: 50000000 },
              { name: 'Other', allocated: 50000000, spent: 0 }
            ],
            budgetItems: [
              { id: 1, category: 'Material', description: 'Besi Beton', amount: 200000000, status: 'approved' },
              { id: 2, category: 'Material', description: 'Semen', amount: 100000000, status: 'approved' },
              { id: 3, category: 'Labor', description: 'Tukang', amount: 100000000, status: 'approved' },
              { id: 4, category: 'Equipment', description: 'Sewa Alat', amount: 50000000, status: 'pending' }
            ]
          });
          setLoading(false);
        }, 1000);

        // Simulasi fetch data proyek
        setTimeout(() => {
          setProjects([
            { id: 1, name: 'Proyek A' },
            { id: 2, name: 'Proyek B' },
            { id: 3, name: 'Proyek C' }
          ]);
        }, 800);
      } catch (error) {
        console.error('Error fetching budget data:', error);
        enqueueSnackbar('Gagal memuat data anggaran', { variant: 'error' });
        setLoading(false);
      }
    };

    fetchBudgetData();
  }, [enqueueSnackbar]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Calculate percentage
  const calculatePercentage = (value, total) => {
    return Math.round((value / total) * 100);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <PageHeader 
          title="Budget Management" 
          icon={<AttachMoneyIcon fontSize="large" />}
          description="Kelola dan pantau anggaran proyek"
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <PageHeader 
        title="Budget Management" 
        icon={<AttachMoneyIcon fontSize="large" />}
        description="Kelola dan pantau anggaran proyek"
      />

      {/* Action Bar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ bgcolor: '#F0C14B', '&:hover': { bgcolor: '#DDA726' } }}
          >
            Tambah Anggaran
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{ color: '#5D4037', borderColor: '#5D4037' }}
          >
            Filter
          </Button>
        </Box>
        <Tooltip title="Download Laporan Anggaran">
          <IconButton color="primary">
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Budget Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#E3F2FD', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Total Anggaran
              </Typography>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatCurrency(budgetData.totalBudget)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Chip label="Aktif" color="primary" size="small" />
                <Typography variant="body2">
                  {projects.length} Proyek
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#FFF8E1', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Terpakai
              </Typography>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatCurrency(budgetData.spent)}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Persentase:</span>
                  <span>{calculatePercentage(budgetData.spent, budgetData.totalBudget)}%</span>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#E8F5E9', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Sisa Anggaran
              </Typography>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatCurrency(budgetData.remaining)}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Persentase:</span>
                  <span>{calculatePercentage(budgetData.remaining, budgetData.totalBudget)}%</span>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Budget Categories */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Kategori Anggaran
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {budgetData.categories.map((category, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {category.name}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(category.allocated)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Terpakai: {formatCurrency(category.spent)} ({calculatePercentage(category.spent, category.allocated)}%)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Budget Items List */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daftar Item Anggaran
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {budgetData.budgetItems.map((item) => (
                <Grid item xs={12} key={item.id}>
                  <Card variant="outlined" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                    <Box>
                      <Typography variant="subtitle1">
                        {item.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Kategori: {item.category}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(item.amount)}
                      </Typography>
                      <Chip 
                        label={item.status === 'approved' ? 'Disetujui' : 'Menunggu'} 
                        color={item.status === 'approved' ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Budget;
